import { useDocumentTitle } from 'client/utils/use-document-title';
import { LoadedProps, RouteLoadProps, useRouter } from '@components/router';
import { rpx } from 'client/lib/rpx-client';
import { AsyncForm, FormGroup } from '@components/async-form';
import { useEffect, useState } from 'preact/hooks';
import { BtnPrimary, Button } from '@components/buttons';
import { IcoArrowRight } from '@components/icons';
import { Field, InputField, eventToState } from '@components/calendar-form';
import { Auth } from 'client/lib/auth';
import { Password } from '@components/password';
import { DefaultSpinner } from '@components/spinner';
import { defCalRoute } from './common';
import { UserProfileIcon } from '@components/avatars';
import { detectTimezone } from 'shared/dateutil';
import { LoginWrapper, FormHeader } from './components/login-wrapper';

export const route = defCalRoute({ isPublic: true, load, Page });

type Props = LoadedProps<typeof load>;

type Pane = 'email' | 'auth' | 'confirm' | 'oneclick';

async function load(props: RouteLoadProps) {
  const host = await rpx.ruzcal.getHost({});

  // Handle one-click login code
  if (props.params.code) {
    try {
      const oneClickUser = await rpx.auth.getOneClickUser({ resetCode: props.params.code });
      return {
        host,
        email: oneClickUser?.email || '',
        name: oneClickUser?.name || '',
        profilePhotoUrl: oneClickUser?.profilePhotoUrl,
        exists: true,
        pane: 'oneclick' as Pane,
        resetCode: props.params.code,
      };
    } catch (err) {
      // Invalid code, fall back to regular login
      return {
        host,
        email: '',
        name: '',
        profilePhotoUrl: undefined,
        exists: false,
        pane: 'email' as Pane,
        error: 'Invalid or expired login code',
      };
    }
  }

  const pane: Pane = props.auth.user ? 'confirm' : 'email';
  return {
    host,
    email: props.auth.user?.email || '',
    name: props.auth.user?.name || '',
    profilePhotoUrl: props.auth.user?.profilePhotoUrl,
    exists: !!props.auth.user,
    pane: pane as unknown as Pane,
  };
}

function AuthForm(props: { email: string; onSubmit(user: Auth['user']): void; redirect?: string }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [state, setState] = useState({
    email: props.email || '',
    password: '',
  });

  async function login(data: { email: string; password: string }) {
    setIsSubmitting(true);
    try {
      const user = await rpx.auth.login(data);
      props.onSubmit(user);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div class="flex flex-col w-full max-w-96 gap-6">
      <FormHeader title="CoachMira" />
      <AsyncForm class="flex flex-col gap-6 w-full max-w-80 mx-auto" onSubmit={login}>
        <InputField
          name="email"
          fullWidth
          autoFocus
          title="Email address"
          placeholder="you@example.com"
          value={state.email}
          onInput={eventToState(setState)}
        />
        <Field name="password" title="Password">
          <Password
            name="password"
            class="p-4"
            placeholder="••••••••"
            value={state.password}
            onInput={eventToState(setState)}
          />
        </Field>
        <BtnPrimary
          class="gap-2 items-center p-4 rounded-full text-base min-w-40 mt-2"
          isLoading={isSubmitting}
        >
          Sign in to CoachMira
          <IcoArrowRight class="size-4 shrink-0" />
        </BtnPrimary>

        <Button
          href={`/calendar/forgot-password?email=${encodeURIComponent(state.email)}`}
          class="flex mx-auto items-center gap-2 text-indigo-600 focus-within:outline-current outline-2 outline-transparent focus-within:shadow-none focus:outline-none"
        >
          Forgot your password?
        </Button>
      </AsyncForm>

      <div class="text-center pt-6 border-t border-gray-200">
        <p class="text-gray-600 text-sm mb-3">Don't have an account?</p>
        <Button
          href="/calendar/signup"
          class="text-indigo-600 hover:text-indigo-700 font-semibold hover:underline"
        >
          Sign up for CoachMira
        </Button>
      </div>
    </div>
  );
}

function OneClickLoginForm(props: {
  user: { name: string; email: string; profilePhotoUrl?: string };
  resetCode: string;
  onSubmit(user: Auth['user']): void;
  error?: string;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (props.error) {
    return (
      <div class="flex flex-col w-full max-w-96 gap-6">
        <FormHeader
          title="Invalid Login Link"
          subtitle="This login link has expired or is invalid. Please request a new one."
        />
        <Button
          href="/calendar/login"
          class="bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-full font-semibold"
        >
          Back to Sign In
        </Button>
      </div>
    );
  }

  async function resetPassword(data: any) {
    setIsSubmitting(true);
    try {
      const user = await rpx.auth.oneClickLogin({
        ...data,
        resetCode: props.resetCode,
        timezone: detectTimezone(),
      });
      if (user) {
        props.onSubmit(user);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div class="flex flex-col w-full max-w-96 gap-6">
      <FormHeader
        title="Welcome back!"
        subtitle="Create a new password to continue to your calendar."
      />

      {/* User info display */}
      <div class="flex items-center border rounded-md p-4 bg-gray-50">
        <UserProfileIcon size="h-12 w-12" user={props.user} />
        <div class="ml-4">
          <div class="font-semibold">{props.user.name}</div>
          <div class="text-gray-600">{props.user.email}</div>
        </div>
      </div>

      <AsyncForm class="flex flex-col gap-6 w-full max-w-80 mx-auto" onSubmit={resetPassword}>
        <FormGroup prop="newPassword">
          <Password
            name="newPassword"
            placeholder="New password"
            class="p-4"
            autoFocus
            autocomplete="new-password"
          />
        </FormGroup>

        <FormGroup prop="confirmPassword">
          <Password
            name="confirmPassword"
            placeholder="Confirm password"
            class="p-4"
            autocomplete="new-password"
          />
        </FormGroup>

        <BtnPrimary
          class="gap-2 items-center p-4 rounded-full text-base min-w-40 mt-2"
          isLoading={isSubmitting}
        >
          Set Password & Continue
          <IcoArrowRight class="size-4 shrink-0" />
        </BtnPrimary>
      </AsyncForm>
    </div>
  );
}

function Page({ params, state, auth }: Props) {
  const router = useRouter();
  const { host } = state;

  useDocumentTitle(['Sign in to CoachMira']);

  const gotoRedirect = () => {
    let redirectUrl = params.redirect ? decodeURIComponent(params.redirect) : '/calendar';
    // Prevent redirect loops - if redirect URL contains /calendar/login, just go to /calendar
    if (redirectUrl.includes('/calendar/login')) {
      redirectUrl = '/calendar';
    }
    router.goto(redirectUrl);
  };

  useEffect(() => {
    if (host) {
      gotoRedirect();
    }
  }, []);

  if (host) {
    return (
      <div class="flex items-center justify-center min-h-screen">
        <DefaultSpinner />
      </div>
    );
  }

  const handleLogin = (user: Auth['user']) => {
    auth.user = user;
    auth.setUser(user);
    gotoRedirect();
  };

  return (
    <LoginWrapper>
      {state.pane === 'oneclick' ? (
        <OneClickLoginForm
          user={{ name: state.name, email: state.email, profilePhotoUrl: state.profilePhotoUrl }}
          resetCode={state.resetCode!}
          onSubmit={handleLogin}
          error={state.error}
        />
      ) : (
        <AuthForm email={params.email} onSubmit={handleLogin} redirect={params.redirect} />
      )}
    </LoginWrapper>
  );
}
